/**
 * AlertLevelSelector Component
 *
 * Multi-select for choosing which alert levels to receive.
 * Supports two layout modes:
 * - "grid": Card-based 2x2 grid layout (default, used in modals)
 * - "list": Full-width list layout (used in settings cards)
 *
 * Design Compliance:
 * - Uses Polaris components and color tokens
 * - Card/list selection patterns with checkbox indicators
 * - Semantic colors for alert levels (critical, warning, info, success)
 * - Accessible keyboard navigation and ARIA attributes
 *
 * @example
 * ```tsx
 * // Grid layout (default)
 * <AlertLevelSelector
 *   selectedLevels={['urgent', 'warning']}
 *   onChange={(levels) => setSelectedLevels(levels)}
 * />
 *
 * // List layout
 * <AlertLevelSelector
 *   layout="list"
 *   selectedLevels={['urgent', 'warning']}
 *   onChange={(levels) => setSelectedLevels(levels)}
 * />
 * ```
 */

import { useCallback } from 'react';
import {
  InlineStack,
  InlineGrid,
  Text,
  Box,
  Icon,
  BlockStack,
} from '@shopify/polaris';
import type { IconProps } from '@shopify/polaris';
import {
  AlertTriangleIcon,
  AlertCircleIcon,
  InfoIcon,
  CheckCircleIcon,
} from '@shopify/polaris-icons';

export type AlertLevel = 'urgent' | 'warning' | 'info' | 'healthy';

interface AlertLevelConfig {
  id: AlertLevel;
  label: string;
  description: string;
  icon: IconProps['source'];
  colors: {
    /** Light background for selected card/row body */
    backgroundLight: string;
    /** Stronger background for icon container */
    iconBackground: string;
    /** Border color (inactive) */
    border: string;
    /** Border color when active/selected */
    activeBorder: string;
    /** Icon tone when selected */
    iconTone: 'critical' | 'warning' | 'info' | 'success';
    /** Text color for label (matches icon tone) */
    textColor: string;
  };
}

const ALERT_LEVELS: AlertLevelConfig[] = [
  {
    id: 'urgent',
    label: 'Urgent alerts',
    description: 'Products about to stock out',
    icon: AlertTriangleIcon,
    colors: {
      backgroundLight: 'rgba(228, 62, 62, 0.06)',
      iconBackground: 'var(--p-color-bg-fill-critical-secondary)',
      border: 'var(--p-color-border-critical-secondary)',
      activeBorder: 'var(--p-color-border-critical)',
      iconTone: 'critical',
      textColor: 'var(--p-color-text-critical)',
    },
  },
  {
    id: 'warning',
    label: 'Warning alerts',
    description: 'Products that need attention',
    icon: AlertCircleIcon,
    colors: {
      backgroundLight: 'rgba(178, 132, 0, 0.06)',
      iconBackground: 'var(--p-color-bg-fill-warning-secondary)',
      border: 'var(--p-color-border-warning-secondary)',
      activeBorder: 'var(--p-color-bg-fill-warning)',
      iconTone: 'warning',
      textColor: 'var(--p-color-text-warning)',
    },
  },
  {
    id: 'info',
    label: 'Info alerts',
    description: 'General inventory updates',
    icon: InfoIcon,
    colors: {
      backgroundLight: 'rgba(32, 110, 199, 0.06)',
      iconBackground: 'var(--p-color-bg-fill-info-secondary)',
      border: 'var(--p-color-border-info-secondary)',
      activeBorder: 'var(--p-color-border-info)',
      iconTone: 'info',
      textColor: 'var(--p-color-text-info)',
    },
  },
  {
    id: 'healthy',
    label: 'Restock alerts',
    description: 'Products back in stock',
    icon: CheckCircleIcon,
    colors: {
      backgroundLight: 'rgba(41, 132, 90, 0.06)',
      iconBackground: 'var(--p-color-bg-fill-success-secondary)',
      border: 'var(--p-color-border-success-secondary)',
      activeBorder: 'var(--p-color-border-success)',
      iconTone: 'success',
      textColor: 'var(--p-color-text-success)',
    },
  },
];

export interface AlertLevelSelectorProps {
  /** Currently selected alert levels */
  selectedLevels: AlertLevel[];
  /** Callback when selection changes */
  onChange: (levels: AlertLevel[]) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Layout mode: "grid" for card-based 2x2, "list" for full-width rows */
  layout?: 'grid' | 'list';
}

export function AlertLevelSelector({
  selectedLevels,
  onChange,
  disabled = false,
  layout = 'grid',
}: AlertLevelSelectorProps) {
  const toggleLevel = useCallback(
    (levelId: AlertLevel) => {
      if (disabled) return;
      const newLevels = selectedLevels.includes(levelId)
        ? selectedLevels.filter((id) => id !== levelId)
        : [...selectedLevels, levelId];
      onChange(newLevels);
    },
    [selectedLevels, onChange, disabled]
  );

  if (layout === 'list') {
    return (
      <BlockStack gap="200">
        {ALERT_LEVELS.map((level) => {
          const isSelected = selectedLevels.includes(level.id);
          return (
            <AlertLevelListItem
              key={level.id}
              level={level}
              isSelected={isSelected}
              onToggle={() => toggleLevel(level.id)}
              disabled={disabled}
            />
          );
        })}
      </BlockStack>
    );
  }

  return (
    <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
      {ALERT_LEVELS.map((level) => {
        const isSelected = selectedLevels.includes(level.id);
        return (
          <AlertLevelCard
            key={level.id}
            level={level}
            isSelected={isSelected}
            onToggle={() => toggleLevel(level.id)}
            disabled={disabled}
          />
        );
      })}
    </InlineGrid>
  );
}

interface AlertLevelCardProps {
  level: AlertLevelConfig;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

function AlertLevelCard({ level, isSelected, onToggle, disabled }: AlertLevelCardProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={isSelected}
      style={{
        width: '100%',
        padding: '12px',
        borderRadius: '8px',
        border: `2px solid ${isSelected ? level.colors.activeBorder : 'var(--p-color-border)'}`,
        background: isSelected ? level.colors.backgroundLight : 'var(--p-color-bg-surface)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        transition: 'all 0.15s ease',
        opacity: disabled ? 0.5 : 1,
        position: 'relative',
      }}
    >
      {/* Visual checkbox indicator in top-right (non-interactive since parent button handles clicks) */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          width: '18px',
          height: '18px',
          borderRadius: '4px',
          border: `2px solid ${isSelected ? 'var(--p-color-border-emphasis)' : 'var(--p-color-border)'}`,
          backgroundColor: isSelected ? 'var(--p-color-bg-fill-brand)' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.15s ease',
        }}
        aria-hidden="true"
      >
        {isSelected && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 3L4.5 8.5L2 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      <InlineStack gap="300" blockAlign="start" wrap={false}>
        {/* Icon container - uses stronger secondary color */}
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: isSelected
              ? level.colors.iconBackground
              : 'var(--p-color-bg-surface-secondary)',
          }}
        >
          <Icon source={level.icon} tone={isSelected ? level.colors.iconTone : 'subdued'} />
        </div>

        {/* Text content */}
        <Box paddingInlineEnd="600">
          <BlockStack gap="050">
            <span
              style={{
                fontSize: 'var(--p-font-size-350)',
                fontWeight: 'var(--p-font-weight-semibold)',
                lineHeight: 'var(--p-font-line-height-500)',
                color: level.colors.textColor,
              }}
            >
              {level.label}
            </span>
            <span
              style={{
                fontSize: 'var(--p-font-size-325)',
                lineHeight: 'var(--p-font-line-height-400)',
                color: 'var(--p-color-text-secondary)',
              }}
            >
              {level.description}
            </span>
          </BlockStack>
        </Box>
      </InlineStack>
    </button>
  );
}

interface AlertLevelListItemProps {
  level: AlertLevelConfig;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

function AlertLevelListItem({
  level,
  isSelected,
  onToggle,
  disabled,
}: AlertLevelListItemProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      role="checkbox"
      aria-checked={isSelected}
      aria-label={`${level.label}: ${level.description}`}
      className="alert-level-list-item"
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--p-space-400)',
        padding: 'var(--p-space-300)',
        borderRadius: 'var(--p-border-radius-200)',
        border: `2px solid ${isSelected ? level.colors.activeBorder : 'var(--p-color-border)'}`,
        background: isSelected ? level.colors.backgroundLight : 'var(--p-color-bg-surface)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textAlign: 'left',
        transition: 'all var(--p-motion-duration-150) var(--p-motion-ease)',
        opacity: disabled ? 0.5 : 1,
        outline: 'none',
      }}
    >
      {/* Circular Icon Container */}
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          background: isSelected ? level.colors.iconBackground : 'var(--p-color-bg-fill-secondary)',
          transition: 'background var(--p-motion-duration-150) var(--p-motion-ease)',
        }}
      >
        <div
          style={{
            color: isSelected ? 'var(--p-color-text-on-color)' : 'var(--p-color-icon-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon source={level.icon} />
        </div>
      </div>

      {/* Label and Description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          <Text
            as="span"
            variant="bodyMd"
            fontWeight="medium"
            tone={isSelected ? undefined : 'subdued'}
          >
            {level.label}
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            {level.description}
          </Text>
        </InlineStack>
      </div>

      {/* Visual checkbox indicator (non-interactive since parent button handles clicks) */}
      <div
        style={{
          flexShrink: 0,
          width: '18px',
          height: '18px',
          borderRadius: '4px',
          border: `2px solid ${isSelected ? 'var(--p-color-border-emphasis)' : 'var(--p-color-border)'}`,
          backgroundColor: isSelected ? 'var(--p-color-bg-fill-brand)' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all var(--p-motion-duration-150) var(--p-motion-ease)',
        }}
        aria-hidden="true"
      >
        {isSelected && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 3L4.5 8.5L2 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </button>
  );
}

export { ALERT_LEVELS };
